import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../auth/AuthContext";
import { api } from "../api";
import { useSse } from "../sse";
import type { FullRecord } from "@shared/types";
import { MetricsRow } from "./MetricsRow";
import { ReactivationPanel } from "./ReactivationPanel";
import { FilterToolbar, type FilterState, initialFilterState } from "./FilterToolbar";
import { RecordTable } from "./RecordTable";
import { EditModal } from "./EditModal";
import { ReactivationModal } from "./ReactivationModal";
import { UsersPanel } from "../users/UsersPanel";
import { ImportBanner } from "./ImportBanner";
import { ChangePasswordScreen } from "../auth/ChangePasswordScreen";
import { exportExcel } from "./exportExcel";

export function CrmShell() {
  const auth = useAuth();
  const [records, setRecords] = useState<FullRecord[]>([]);
  const [filter, setFilter] = useState<FilterState>(initialFilterState());
  const [editingId, setEditingId] = useState<number | null | undefined>(undefined);
  const [reativId, setReativId] = useState<number | null>(null);
  const [showUsers, setShowUsers] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const cursorRef = useRef(0);
  const [sseReady, setSseReady] = useState(false);

  useEffect(() => {
    const cached = localStorage.getItem("crm_ss_cache_v1");
    if (cached) {
      try {
        const obj = JSON.parse(cached);
        if (Array.isArray(obj.records)) {
          setRecords(obj.records);
          cursorRef.current = obj.cursor || 0;
        }
      } catch {}
    }
    api.records().then((res) => {
      if (!res) return;
      setRecords(res.records);
      cursorRef.current = res.cursor;
      localStorage.setItem("crm_ss_cache_v1", JSON.stringify({ records: res.records, cursor: res.cursor }));
    }).catch(() => {}).finally(() => {
      setSseReady(true);
    });
  }, []);

  useEffect(() => {
    localStorage.setItem("crm_ss_cache_v1", JSON.stringify({ records, cursor: cursorRef.current }));
  }, [records]);

  useSse(
    sseReady,
    cursorRef.current,
    useCallback(async (recordIds: number[], newCursor: number) => {
      cursorRef.current = newCursor;
      try {
        const fresh = await api.records(recordIds);
        if (!fresh) return;
        setRecords((prev) => {
          const byId = new Map(prev.map(r => [r.id, r]));
          for (const r of fresh.records) byId.set(r.id, r);
          for (const id of fresh.deleted) byId.delete(id);
          return [...byId.values()].sort((a, b) => b.id - a.id);
        });
      } catch {}
    }, []),
    useCallback(() => { auth.logout(); }, [auth]),
  );

  const upsertRecord = useCallback((r: FullRecord) => {
    setRecords((prev) => {
      const i = prev.findIndex(x => x.id === r.id);
      if (i >= 0) { const next = [...prev]; next[i] = r; return next; }
      return [r, ...prev];
    });
  }, []);

  const removeRecord = useCallback((id: number) => {
    setRecords((prev) => prev.filter(x => x.id !== id));
  }, []);

  if (showChangePw) return <ChangePasswordScreen requireCurrent={true} onSuccess={() => setShowChangePw(false)} />;

  return (
    <>
      <div className="topbar">
        <div className="topbar-brand">
          <div className="logo"><svg viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></div>
          <div><div className="t-name">S&S Consultoria Securitária</div><div className="t-sub">CRM de Prospecções</div></div>
        </div>
        <div className="t-actions">
          {auth.user.is_admin && <button className="btn" onClick={() => setShowUsers(true)}>Usuários</button>}
          <button className="btn" onClick={() => exportExcel(records)}>
            <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Exportar Excel
          </button>
          <button className="btn btn-p" onClick={() => setEditingId(null)}>
            <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Nova prospecção
          </button>
          <button className="btn" onClick={() => setShowChangePw(true)}>Senha</button>
          <button className="btn" onClick={() => auth.logout()}>Sair</button>
        </div>
      </div>

      <div className="main">
        <ImportBanner hasImportedLocal={auth.user.has_imported_local} onImported={() => { api.records().then(r => r && setRecords(r.records)); }} />
        <MetricsRow records={records} />
        <ReactivationPanel records={records} onOpen={setEditingId} onReativate={setReativId} />
        <FilterToolbar records={records} filter={filter} onChange={setFilter} />
        <RecordTable records={records} filter={filter} onOpen={setEditingId} />
      </div>

      {editingId !== undefined && (
        <EditModal
          recordId={editingId}
          records={records}
          onUpsert={upsertRecord}
          onRemove={removeRecord}
          onClose={() => setEditingId(undefined)}
        />
      )}

      {reativId != null && (
        <ReactivationModal
          recordId={reativId}
          records={records}
          onUpsert={upsertRecord}
          onClose={() => setReativId(null)}
        />
      )}

      {showUsers && <UsersPanel onClose={() => setShowUsers(false)} />}
    </>
  );
}
